import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {Header3} from 'ui-library/Headers';
import runModal from 'ui-library/runModal';

import {setBenchlingAppDefinitionRedirectUris} from '../AppDefinitionDAO';
import {
  deleteClientSecret,
  regenerateClientSecret,
  UpdateBenchlingAppDefinitionArgs,
} from '../BenchlingAppDAO';
import {getInstallationOrDefinitionId} from '../lib';
import {ClientCredentialSectionCredential} from './__generated/ClientCredentialSectionCredential';
import {AppDefinitionWithOAuth} from './BenchlingGlobalAppPage';
import ClientCredentialSection from './ClientCredentialSection';
import DisplayClientSecretModal from './DisplayClientSecretModal';
import RedirectUriSection from './RedirectUriSection';

type OAuthCredential = ExtractByTypename<
  ClientCredentialSectionCredential['oauthCredential'],
  'OAuthCredentialNode'
>;

export function BenchlingAppCredentials({
  appDefinition,
  refetchAppDefinition,
  handleSubmitDefinition,
}: {
  appDefinition: AppDefinitionWithOAuth;
  refetchAppDefinition: () => void;
  handleSubmitDefinition: (newValue: UpdateBenchlingAppDefinitionArgs) => void;
}): JSX.Element {
  async function handleRegenerateClientSecret(): Promise<void> {
    const appInstallationOrDefinitionId = getInstallationOrDefinitionId(appDefinition);
    const rv = await regenerateClientSecret(appInstallationOrDefinitionId);
    runModal(DisplayClientSecretModal, {
      clientSecret: rv.clientSecret,
      onRequestClose: () => {
        refetchAppDefinition();
        notifications.success('Successfully generated a new Client Secret');
      },
      onRequestSubmit: () => {
        refetchAppDefinition();
        notifications.success('Successfully generated a new Client Secret');
      },
    });
  }
  async function handleDeleteClientSecret(): Promise<void> {
    const appInstallationOrDefinitionId = getInstallationOrDefinitionId(appDefinition);
    await deleteClientSecret(appInstallationOrDefinitionId);
    refetchAppDefinition();
    notifications.success('Successfully deleted Client Secret');
  }
  async function handleSetRedirectUris(redirectUris: Array<string>): Promise<void> {
    const appInstallationOrDefinitionId = getInstallationOrDefinitionId(appDefinition);
    await setBenchlingAppDefinitionRedirectUris(appInstallationOrDefinitionId, {redirectUris});
    refetchAppDefinition();
    notifications.success('Successfully updated Redirect URIs');
  }

  const currentVersion = appDefinition.latestReleaseVersion!;
  return (
    <div>
      <Header3 className={css(styles.sectionHead)}>Credentials</Header3>
      {appDefinition.oauthCredential && !isInaccessible(appDefinition.oauthCredential) && (
        <>
          <ClientCredentialSection
            appName={currentVersion.name}
            clientSecrets={(appDefinition.oauthCredential as OAuthCredential).clientSecrets}
            clientId={(appDefinition.oauthCredential as OAuthCredential).clientId}
            handleDelete={handleDeleteClientSecret}
            handleRegenerateSecret={handleRegenerateClientSecret}
          />
          {BenchlingConfig.IDENTITY_ENABLE_OAUTH_AUTHORIZATION_CODE_FLOW && (
            <RedirectUriSection
              redirectUris={appDefinition.oauthCredential.redirectUris}
              handleSetRedirectUris={handleSetRedirectUris}
            />
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  pullLeft: {
    marginLeft: '0px',
  },
  noRowIndent: {
    marginLeft: '-15px',
  },
  sectionHead: {
    marginTop: '16px',
  },
  subheader: {
    marginBottom: '7px',
  },
});
